import { TableCellsIcon } from '@heroicons/react/24/outline';
import { InvoicePreview } from '../../../../queries/billing/use-upcoming-invoice';
import LoadingCell from './LoadingCell';

function formatCentsToDollars(value: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
    }).format(value / 100);
}

type InvoiceTableProps = {
    isLoading: boolean;
    invoice?: InvoicePreview;
};
function InvoiceTable({ isLoading, invoice }: InvoiceTableProps): JSX.Element {
    if (!isLoading && !invoice) {
        return (
            <div className="flex flex-col items-center">
                <TableCellsIcon className="h-6 w-6 text-contentSecondary" />
                <p className="text-contentSecondary text-sm mt-1">Unable to load invoice. You may need to add a payment method first.</p>
            </div>
        );
    }
    return (
        <table className="w-full mt-8">
            <thead>
                <tr className="border-b">
                    <th className="text-contentPrimary text-xs font-bold uppercase text-left pb-2">Description</th>
                    <th className="text-contentPrimary text-xs font-bold uppercase text-right pb-2">Quantity</th>
                    <th className="text-contentPrimary text-xs font-bold uppercase text-right pb-2">Unit Price</th>
                    <th className="text-contentPrimary text-xs font-bold uppercase text-right pb-2">Total</th>
                </tr>
            </thead>

            <tbody>
                {isLoading && (
                    <>
                        <tr>
                            <LoadingCell colSpan={4} />
                        </tr>
                        <tr>
                            <LoadingCell colSpan={4} />
                        </tr>
                    </>
                )}
                {!isLoading &&
                    invoice?.lineItems.map((item, idx) => (
                        <tr className="border-b" key={idx}>
                            <td className="text-contentSecondary text-sm text-left py-2">{item.description}</td>
                            <td className="text-contentSecondary text-sm text-right py-2">{item.quantity}</td>
                            <td className="text-contentSecondary text-sm text-right py-2">
                                {formatCentsToDollars(item.unitAmount, invoice.currency)}
                            </td>
                            <td className="text-contentSecondary text-sm text-right py-2">
                                {formatCentsToDollars(item.total, invoice.currency)}
                            </td>
                        </tr>
                    ))}
            </tbody>
            <tfoot>
                {/* Subtotal */}
                <tr>
                    <td colSpan={2}></td>
                    {isLoading && (
                        <>
                            <LoadingCell />
                            <LoadingCell />
                        </>
                    )}
                    {!isLoading && (
                        <>
                            <td className="text-contentSecondary text-sm text-right py-2">Subtotal</td>
                            <td className="text-contentSecondary text-sm text-right py-2">
                                {formatCentsToDollars(invoice!.subtotal, invoice!.currency)}
                            </td>
                        </>
                    )}
                </tr>
                {/* Coupons */}
                <tr>
                    <td colSpan={2}></td>
                    {isLoading && (
                        <>
                            <LoadingCell />
                            <LoadingCell />
                        </>
                    )}
                    {!isLoading && (
                        <>
                            <td className="text-contentSecondary text-sm text-right py-2">Discount</td>
                            <td className="text-contentSecondary text-sm text-right py-2">
                                {formatCentsToDollars(-invoice!.discount, invoice!.currency)}
                            </td>
                        </>
                    )}
                </tr>
                {/* Total excluding tax */}
                <tr>
                    <td colSpan={2}></td>
                    {isLoading && (
                        <>
                            <LoadingCell />
                            <LoadingCell />
                        </>
                    )}
                    {!isLoading && (
                        <>
                            <td className="text-contentSecondary text-sm text-right py-2">Total excluding tax</td>
                            <td className="text-contentSecondary text-sm text-right py-2">
                                {formatCentsToDollars(invoice!.subtotal - invoice!.discount, invoice!.currency)}
                            </td>
                        </>
                    )}
                </tr>
                {/* Tax */}
                <tr>
                    <td colSpan={2}></td>
                    {isLoading && (
                        <>
                            <LoadingCell />
                            <LoadingCell />
                        </>
                    )}
                    {!isLoading && (
                        <>
                            <td className="text-contentSecondary text-sm text-right py-2">
                                {invoice!.tax.name} ({invoice!.tax.rate}% on{' '}
                                {formatCentsToDollars(invoice!.subtotal - invoice!.discount, invoice!.currency)})
                            </td>
                            <td className="text-contentSecondary text-sm text-right py-2">
                                {formatCentsToDollars(invoice!.tax.amount, invoice!.currency)}
                            </td>
                        </>
                    )}
                </tr>
                {/* Total */}
                <tr className="border-t">
                    <td colSpan={2}></td>
                    {isLoading && (
                        <>
                            <LoadingCell />
                            <LoadingCell />
                        </>
                    )}
                    {!isLoading && (
                        <>
                            <td className="text-contentPrimary text-sm text-right py-2 font-bold">Total</td>
                            <td className="text-contentPrimary text-sm text-right py-2 font-bold">
                                {formatCentsToDollars(invoice!.total, invoice!.currency)}
                            </td>
                        </>
                    )}
                </tr>
            </tfoot>
        </table>
    );
}

export default InvoiceTable;
